import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, ExternalLink, AlertCircle } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';

const BackorderUpdateModal = ({ item, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    status: item.status || 'pending',
    fulfilledQty: item.fulfilled_qty || 0,
    orderboardId: item.orderboard_id || '',
    notes: item.notes || ''
  });

  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const validateForm = () => {
    // Fulfilled quantity cannot be negative
    if (formData.fulfilledQty < 0) {
      setValidationError('Fulfilled quantity cannot be negative');
      return false;
    }

    // Fulfilled quantity cannot exceed backorder quantity
    if (formData.fulfilledQty > item.backorder_qty) {
      setValidationError(`Fulfilled quantity cannot exceed ordered quantity (${item.backorder_qty})`);
      return false;
    }

    // If status is fulfilled, fulfilled qty should equal backorder qty
    if (formData.status === 'fulfilled' && formData.fulfilledQty !== item.backorder_qty) {
      setValidationError(`For 'Fulfilled' status, fulfilled quantity must equal ${item.backorder_qty}`);
      return false;
    }

    // If status is partially fulfilled, fulfilled qty should be between 0 and backorder qty
    if (formData.status === 'partially_fulfilled' && (formData.fulfilledQty === 0 || formData.fulfilledQty === item.backorder_qty)) {
      setValidationError('For \'Partially Fulfilled\' status, fulfilled quantity must be between 0 and total quantity');
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user found');

      // Call the database function to update backorder item status
      const { data, error } = await supabase.rpc('update_backorder_item_status', {
        p_order_id: item.order_id,
        p_part_number: item.part_number,
        p_new_status: formData.status,
        p_fulfilled_qty: parseInt(formData.fulfilledQty),
        p_orderboard_id: formData.orderboardId || null,
        p_notes: formData.notes || null,
        p_updated_by: user.id
      });

      if (error) throw error;

      if (data.success) {
        showNotification(
          `Backorder status updated: ${data.previousStatus} → ${data.newStatus}`,
          'success'
        );
        onUpdate();
      } else {
        throw new Error(data.error || 'Failed to update backorder status');
      }
    } catch (error) {
      console.error('Error updating backorder status:', error);
      showError(error, 'Failed to update backorder status');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    setFormData(prev => ({
      ...prev,
      status: newStatus
    }));

    // Auto-adjust fulfilled quantity based on status
    if (newStatus === 'fulfilled') {
      setFormData(prev => ({
        ...prev,
        fulfilledQty: item.backorder_qty
      }));
    } else if (newStatus === 'pending') {
      setFormData(prev => ({
        ...prev,
        fulfilledQty: 0
      }));
    }

    setValidationError('');
  };

  const handleFulfilledQtyChange = (value) => {
    const qty = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      fulfilledQty: qty
    }));
    setValidationError('');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      partially_fulfilled: 'bg-orange-100 text-orange-800 border-orange-300',
      fulfilled: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[status] || colors.pending;
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Update Backorder Status</h2>
                <p className="text-sm text-gray-600 mt-1">Manually update the status of this backorder item</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit} className="p-6">
              {/* Item Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600">Customer</p>
                    <p className="text-sm font-medium text-gray-900">{item.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Order Number</p>
                    <p className="text-sm font-medium text-gray-900">{item.order_number}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-600">Part Number</p>
                    <p className="text-sm font-mono font-medium text-gray-900">{item.part_number}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-600">Description</p>
                    <p className="text-sm text-gray-900">{item.description}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Ordered Quantity</p>
                    <p className="text-sm font-bold text-gray-900">{item.backorder_qty}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Current Status</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                      {item.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-gray-900"
                  >
                    <option value="pending">Pending</option>
                    <option value="partially_fulfilled">Partially Fulfilled</option>
                    <option value="fulfilled">Fulfilled</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Fulfilled Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fulfilled Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={item.backorder_qty}
                    value={formData.fulfilledQty}
                    onChange={(e) => handleFulfilledQtyChange(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum: {item.backorder_qty} | Remaining: {item.backorder_qty - formData.fulfilledQty}
                  </p>
                  {formData.status === 'partially_fulfilled' && (
                    <div className="mt-2">
                      <div className="bg-orange-50 border border-orange-200 rounded p-2">
                        <p className="text-xs text-orange-800">
                          Progress: {Math.round((formData.fulfilledQty / item.backorder_qty) * 100)}% complete
                        </p>
                        <div className="w-full bg-orange-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-orange-600 h-2 rounded-full transition-all"
                            style={{ width: `${(formData.fulfilledQty / item.backorder_qty) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* OrderBoard ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    OrderBoard ID <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={formData.orderboardId}
                      onChange={(e) => setFormData(prev => ({ ...prev, orderboardId: e.target.value }))}
                      placeholder="e.g., 12345"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-gray-900"
                    />
                    {formData.orderboardId && (
                      <a
                        href={`http://warehouse.mhs.com.au/orderboard/items/${formData.orderboardId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        title="View in OrderBoard"
                      >
                        <ExternalLink className="w-4 h-4 text-gray-600" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows="3"
                    placeholder="Add any additional notes about this update..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-gray-900 resize-none"
                  />
                </div>

                {/* Validation Error */}
                {validationError && (
                  <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{validationError}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !!validationError}
                  className="flex items-center px-4 py-2 bg-brand text-brand-dark rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-dark mr-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default BackorderUpdateModal;
